import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  GitBranch,
  Network,
  Tags,
  ExternalLink,
  Sparkles,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import {
  resolutionApi,
  type CanonicalEntityData,
  type CanonicalOntologyData,
} from '@/services/api';

interface CatalogMembershipsTabProps {
  canonical: CanonicalEntityData;
}

interface ConceptTypeBreadcrumb {
  id: string;
  canonical_name: string;
  display_name?: string;
}

function formatClassLabel(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function CatalogMembershipsTab({ canonical }: CatalogMembershipsTabProps) {
  const [ontology, setOntology] = useState<CanonicalOntologyData | null>(null);
  const [ontologyLoading, setOntologyLoading] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [reclassifyStatus, setReclassifyStatus] = useState<string | null>(null);

  const [conceptTypePath, setConceptTypePath] = useState<ConceptTypeBreadcrumb[]>([]);
  const [conceptTypePathLoading, setConceptTypePathLoading] = useState(false);

  // Load UOS ontology detail.
  useEffect(() => {
    setOntologyLoading(true);
    resolutionApi
      .getCanonicalOntology(canonical.id)
      .then(setOntology)
      .catch(() => setOntology(null))
      .finally(() => setOntologyLoading(false));
  }, [canonical.id]);

  // Load ConceptType hierarchy path for market/consumer concepts.
  useEffect(() => {
    const relevant =
      canonical.entity_type === 'market_concept' ||
      canonical.entity_type === 'consumer_concept';
    if (!relevant) {
      setConceptTypePath([]);
      return;
    }

    const data = (canonical.canonical_data ?? {}) as Record<string, unknown>;
    const typeId =
      typeof data.concept_type_id === 'string' ? (data.concept_type_id as string) : null;
    if (!typeId) {
      setConceptTypePath([]);
      return;
    }

    // Pull the full concept_type corpus and walk the parent chain client-side.
    setConceptTypePathLoading(true);
    resolutionApi
      .listCanonicals({ entity_type: 'concept_type', limit: 200 })
      .then((res) => {
        const byId = new Map(res.canonicals.map((c) => [c.id, c]));
        const path: ConceptTypeBreadcrumb[] = [];
        let cur = byId.get(typeId);
        const seen = new Set<string>();
        while (cur && !seen.has(cur.id)) {
          seen.add(cur.id);
          const cData = (cur.canonical_data ?? {}) as Record<string, unknown>;
          path.unshift({
            id: cur.id,
            canonical_name: cur.canonical_name,
            display_name:
              typeof cData.display_name === 'string' ? (cData.display_name as string) : undefined,
          });
          const parentId =
            typeof cData.parent_type_id === 'string'
              ? (cData.parent_type_id as string)
              : null;
          cur = parentId ? byId.get(parentId) : undefined;
        }
        setConceptTypePath(path);
      })
      .catch(() => setConceptTypePath([]))
      .finally(() => setConceptTypePathLoading(false));
  }, [canonical.id, canonical.entity_type, canonical.canonical_data]);

  // Ad Taxonomy references — surface market_concept hits only; others rarely appear.
  const adTaxonomyPlaceholder = useMemo(
    () => ({
      applicable:
        canonical.entity_type === 'market_concept' ||
        canonical.entity_type === 'consumer_concept',
    }),
    [canonical.entity_type],
  );

  const handleReclassify = async () => {
    setReclassifying(true);
    setReclassifyStatus(null);
    try {
      await resolutionApi.classifyCanonical(canonical.id);
      setReclassifyStatus(
        'Reclassification job started — refresh in a few seconds to see the updated UOS class.',
      );
    } catch (e) {
      setReclassifyStatus((e as Error).message);
    } finally {
      setReclassifying(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* UOS membership */}
      <Section
        Icon={GitBranch}
        title="Universal Ontological Schema"
        subtitle="Philosophical classification"
        href={
          ontology?.ontological_class
            ? `/knowledge/catalogs/ontology`
            : undefined
        }
      >
        {ontologyLoading ? (
          <div className="text-sm text-slate-400">
            <Loader2 size={12} className="inline animate-spin mr-1" /> Loading UOS…
          </div>
        ) : ontology?.ontological_class ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="text-[11px] font-bold uppercase px-2 py-0.5 rounded-full border"
                style={{
                  borderColor: ontology.ontological_class_color ?? '#94a3b8',
                  color: ontology.ontological_class_color ?? '#475569',
                }}
              >
                {ontology.ontological_class_label ??
                  formatClassLabel(ontology.ontological_class)}
              </span>
              <span className="text-xs text-emerald-600 font-semibold">
                {(ontology.ontological_classification_confidence * 100).toFixed(0)}%
              </span>
              {ontology.ontological_classified_at && (
                <span className="text-[10px] text-slate-400">
                  classified {new Date(ontology.ontological_classified_at).toLocaleDateString()}
                </span>
              )}
            </div>
            {ontology.ontological_class_description && (
              <p className="text-xs text-slate-600">{ontology.ontological_class_description}</p>
            )}
            {ontology.proposed_new_class && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 text-amber-700">
                Agent proposed a new class: <code>{ontology.proposed_new_class}</code> —
                pending human review.
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={handleReclassify}
                disabled={reclassifying}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-40"
              >
                {reclassifying ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Sparkles size={11} />
                )}
                Reclassify
              </button>
              <Link
                to={`/knowledge/estate/entities?uos_class=${encodeURIComponent(
                  ontology.ontological_class,
                )}`}
                className="inline-flex items-center gap-1 text-xs text-brand-teal hover:text-brand-teal-dark"
              >
                Explore class members
                <ArrowRight size={11} />
              </Link>
            </div>
            {reclassifyStatus && (
              <p className="text-xs text-slate-500 italic">{reclassifyStatus}</p>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Not yet classified.</span>
            <button
              onClick={handleReclassify}
              disabled={reclassifying}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold bg-brand-teal text-navy-900 rounded-lg hover:bg-brand-teal/90 disabled:opacity-40"
            >
              {reclassifying ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <Sparkles size={11} />
              )}
              Classify now
            </button>
          </div>
        )}
      </Section>

      {/* ConceptType hierarchy */}
      {(canonical.entity_type === 'market_concept' ||
        canonical.entity_type === 'consumer_concept') && (
        <Section
          Icon={Network}
          title="Concept Taxonomy"
          subtitle="IS-A hierarchy path"
          href="/knowledge/catalogs/concepts"
        >
          {conceptTypePathLoading ? (
            <div className="text-sm text-slate-400">
              <Loader2 size={12} className="inline animate-spin mr-1" /> Resolving path…
            </div>
          ) : conceptTypePath.length === 0 ? (
            <div className="text-sm text-slate-500">
              No ConceptType linked to this entity yet.
            </div>
          ) : (
            <div className="flex items-center gap-1.5 flex-wrap text-sm">
              {conceptTypePath.map((n, i) => (
                <span key={n.id} className="flex items-center gap-1.5">
                  <Link
                    to={`/knowledge/catalogs/concepts`}
                    className="text-slate-700 hover:text-brand-teal-dark"
                  >
                    {n.display_name ?? n.canonical_name}
                  </Link>
                  {i < conceptTypePath.length - 1 && (
                    <ArrowRight size={10} className="text-slate-400" />
                  )}
                </span>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Ad Taxonomy references */}
      <Section
        Icon={Tags}
        title="Ad Taxonomy"
        subtitle="Creative references"
        href="/knowledge/catalogs/ad-taxonomy"
      >
        {adTaxonomyPlaceholder.applicable ? (
          <div className="text-sm text-slate-500">
            Ad Taxonomy cross-refs depend on per-client AdCreativeDefinition data.
            Open the Ad Taxonomy catalog to browse references for this concept.
          </div>
        ) : (
          <div className="text-sm text-slate-400 italic">
            Ad Taxonomy references surface only for market_concept and consumer_concept
            entities (entity_type = <code>{canonical.entity_type}</code>).
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({
  Icon,
  title,
  subtitle,
  href,
  children,
}: {
  Icon: React.ElementType;
  title: string;
  subtitle?: string;
  href?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-brand-teal" />
          <div>
            <h3 className="text-sm font-semibold text-navy-900">{title}</h3>
            {subtitle && <div className="text-[10px] text-slate-400">{subtitle}</div>}
          </div>
        </div>
        {href && (
          <Link
            to={href}
            className="inline-flex items-center gap-1 text-xs text-brand-teal hover:text-brand-teal-dark"
          >
            Explore in Catalog
            <ExternalLink size={11} />
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}
